import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Plus, Trash2, CheckCircle2, Loader2, Receipt, Upload, X, FileDown, RefreshCw,
} from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import useDownloadPdf from "../hooks/useDownloadPdf";
import useToolPageCms from "../hooks/useToolPageCms";
import { ToolPageBanner, ToolPageCtaBand } from "../components/ToolPageCms";
import NAWAddressBlock from "../components/admin/sections/NAWAddressBlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_URL = `${BACKEND_URL}/api/public/invoice-generator`;

const FALLBACK_CMS = {
  page_banner: {
    eyebrow: "Factuur maken",
    title: "Maak in 1 minuut een",
    highlight: "professionele factuur",
    subtitle:
      "Stel afzender, ontvanger, zending en lijntjes in — wij genereren direct een PDF-factuur die u meteen kunt downloaden.",
    badges: [
      { icon: "receipt", label: "PDF direct downloadbaar" },
      { icon: "shield",  label: "Veilige verwerking" },
      { icon: "support", label: "5999Cargo huisstijl" },
    ],
  },
  intro: {},
  cta_band: {
    title: "Vragen over uw factuur?",
    description: "Onze klantenservice helpt u graag verder met douane- of bedrijfsfacturen.",
    cta_primary_label: "Mail ons",
    cta_primary_url: "mailto:info@5999cargo.nl",
    cta_secondary_label: "Bel ons",
    cta_secondary_url: "tel:+31488235999",
  },
};

const EMPTY_LINE = { description: "", quantity: 1, unit_price: 0 };
const EMPTY_PARTY = {
  name: "", company: "",
  street: "", house_number: "", house_number_addition: "",
  postal_code: "", address: "", postcode: "",
  city: "", country: "", email: "", phone: "",
};

function _composeAddress(street, house_number, addition) {
  const parts = [];
  if (street) parts.push(street);
  const hn = [house_number, addition].filter(Boolean).join("");
  if (hn) parts.push(hn);
  return parts.join(" ").trim();
}

// Sprint 3b — 6 Mrt 2026: XCG vervangt ANG voor nieuwe facturen.
import { SUPPORTED_CURRENCIES } from "../lib/currencies";
const CURRENCIES = SUPPORTED_CURRENCIES;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtMoney(v, currency) {
  const n = Number(v) || 0;
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency, minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

export default function FactuurMakenPage() {
  const { t, lang, setLang } = useI18n();
  const { data: cms } = useToolPageCms("factuur-maken", FALLBACK_CMS);
  // Document language defaults to the UI language so the customer immediately
  // sees the PDF in the language they're browsing in. Customers can flip it
  // independently though (useful when the office runs the form on a Dutch
  // laptop but the receiver is English-speaking).
  const [docLang, setDocLang] = useState(lang === "en" ? "en" : "nl");
  const [currency, setCurrency] = useState("EUR");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [sender, setSender] = useState({ ...EMPTY_PARTY, country: "Nederland" });
  const [receiver, setReceiver] = useState({ ...EMPTY_PARTY, country: "Curaçao" });
  const [shipment, setShipment] = useState({
    destination: "Curaçao", w_number: "", description: "", boxes: "", remarks: "",
  });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [vatPercent, setVatPercent] = useState(0);
  const [decls, setDecls] = useState({ value_declaration: false, truthful: false });

  const [logoId, setLogoId] = useState(null);
  const [logoFilename, setLogoFilename] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const [success, setSuccess] = useState(null); // { invoice_number, email_copy_status }
  const [error, setError] = useState("");

  // Phase 18.7 — shared PDF download UX. The hook handles loading state,
  // 3-s slow-load hint, blob download trigger, success/error toasts, and
  // an in-flight guard that prevents double-submits (= duplicate invoice
  // numbers + duplicate document_copy emails).
  const pdf = useDownloadPdf({ lang: docLang });
  const submitting = pdf.loading;

  const totals = useMemo(() => {
    const subtotal = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const vatAmount = subtotal * (Number(vatPercent) || 0) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      total: Math.round((subtotal + vatAmount) * 100) / 100,
    };
  }, [lines, vatPercent]);

  const setParty = (which) => (k) => (e) =>
    which === "sender"
      ? setSender((s) => ({ ...s, [k]: e.target.value }))
      : setReceiver((s) => ({ ...s, [k]: e.target.value }));
  const setShip = (k) => (e) => setShipment((s) => ({ ...s, [k]: e.target.value }));

  const setLine = (idx, k, v) =>
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((arr) => [...arr, { ...EMPTY_LINE }]);
  const removeLine = (idx) =>
    setLines((arr) => (arr.length === 1 ? arr : arr.filter((_, i) => i !== idx)));

  const onChangeDocLang = (next) => {
    setDocLang(next);
    setLang(next); // also flip the UI labels so the form matches the PDF
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${PUBLIC_URL}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoId(r.data.logo_id);
      setLogoFilename(r.data.filename || file.name);
      toast.success(t("factuur.logo_uploaded"));
    } catch (err) {
      const detail = err?.response?.data?.detail || t("factuur.err_logo_failed");
      toast.error(detail);
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const clearLogo = () => {
    setLogoId(null);
    setLogoFilename("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanedLines = lines
      .filter((l) => l.description && l.description.trim())
      .map((l) => ({
        description: l.description.trim(),
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
      }));
    if (cleanedLines.length === 0) {
      setError(t("factuur.err_at_least_one_line"));
      return;
    }
    if (!sender.name || !sender.email || !(sender.street || sender.address) || !sender.city || !sender.country) {
      setError(t("factuur.err_missing_required"));
      return;
    }
    if (!receiver.name || !(receiver.street || receiver.address) || !receiver.city || !receiver.country) {
      setError(t("factuur.err_missing_required"));
      return;
    }
    if (!shipment.destination) {
      setError(t("factuur.err_missing_required"));
      return;
    }
    if (!decls.value_declaration || !decls.truthful) {
      setError(t("factuur.err_declarations"));
      return;
    }

    // P1 — Compose legacy `address` + `postcode` velden vanuit NAW-structuur
    // zodat de bestaande backend PDF-generator zonder wijziging blijft werken.
    const composeParty = (p) => {
      const composed = _composeAddress(p.street, p.house_number, p.house_number_addition);
      return {
        ...p,
        address: composed || p.address || "",
        postcode: p.postal_code || p.postcode || "",
      };
    };

    const payload = {
      language: docLang,
      currency,
      invoice_date: invoiceDate,
      customer_logo_id: logoId || undefined,
      sender: {
        ...composeParty(sender),
        // Pydantic EmailStr will reject empty strings; trim first
        email: (sender.email || "").trim(),
      },
      receiver: {
        ...composeParty(receiver),
        email: (receiver.email || "").trim() || undefined,
      },
      shipment: {
        destination: shipment.destination.trim(),
        w_number: shipment.w_number || undefined,
        description: shipment.description || undefined,
        boxes: shipment.boxes === "" ? undefined : Number(shipment.boxes) || undefined,
        remarks: shipment.remarks || undefined,
      },
      line_items: cleanedLines,
      vat_percent: Number(vatPercent) || 0,
      declarations: decls,
    };

    // Captured at outer scope so onSuccess / parseErrorMessage callbacks
    // below can mutate the page state with the response headers.
    let invNo = "";
    let emailStatus = "unknown";

    await pdf.download(
      async () => {
        const r = await axios.post(`${PUBLIC_URL}/generate`, payload, {
          responseType: "blob",
        });
        invNo = r.headers?.["x-invoice-number"] || r.headers?.["X-Invoice-Number"] || "";
        emailStatus = r.headers?.["x-email-copy-status"] || r.headers?.["X-Email-Copy-Status"] || "unknown";
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `${invNo || "invoice"}.pdf`,
        };
      },
      {
        successMessage: t("factuur.success_intro"),
        errorMessage: t("factuur.err_submit_failed"),
        parseErrorMessage: async (err) => {
          // Blob responses hide their JSON body — read it back so we surface
          // the real Pydantic / FastAPI validation message inline.
          if (err?.response?.data instanceof Blob) {
            try {
              const txt = await err.response.data.text();
              try {
                const parsed = JSON.parse(txt);
                if (Array.isArray(parsed?.detail)) {
                  return parsed.detail.map((p) => p.msg || JSON.stringify(p)).join(" · ");
                }
                return parsed?.detail || txt || null;
              } catch { return txt || null; }
            } catch { return null; }
          }
          return err?.response?.data?.detail || null;
        },
        onSuccess: () => {
          setSuccess({ invoice_number: invNo || "—", email_copy_status: emailStatus });
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
        onError: (err) => {
          // We don't have the parsed message here (parseErrorMessage runs
          // before the hook's toast). Recompute for the inline banner.
          let msg = t("factuur.err_submit_failed");
          if (err?.response?.data instanceof Blob) {
            err.response.data.text().then((txt) => {
              try {
                const parsed = JSON.parse(txt);
                msg = parsed?.detail || msg;
                if (Array.isArray(parsed?.detail)) {
                  msg = parsed.detail.map((p) => p.msg || JSON.stringify(p)).join(" · ");
                }
              } catch { msg = txt || msg; }
              setError(msg);
            }).catch(() => setError(msg));
          } else if (err?.response?.data?.detail) {
            setError(err.response.data.detail);
          } else {
            setError(msg);
          }
        },
      },
    );
  };

  const resetForm = () => {
    setSuccess(null);
    setError("");
    setSender({ ...EMPTY_PARTY, country: "Nederland" });
    setReceiver({ ...EMPTY_PARTY, country: "Curaçao" });
    setShipment({ destination: "Curaçao", w_number: "", description: "", boxes: "", remarks: "" });
    setLines([{ ...EMPTY_LINE }]);
    setVatPercent(0);
    setDecls({ value_declaration: false, truthful: false });
    setLogoId(null);
    setLogoFilename("");
    setInvoiceDate(todayISO());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <SEO title={t("factuur.meta_title")} description={t("factuur.meta_description")} />
      <main className="min-h-screen bg-slate-50" data-testid="factuur-page">
        <ToolPageBanner
          banner={cms?.page_banner || FALLBACK_CMS.page_banner}
          image="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=2000&q=80"
          imageAlt="5999Cargo invoice generator"
          eyebrowIcon={Receipt}
          testid="factuur-banner"
        />

        <div className="container-x py-10 lg:py-16">
          {success ? (
            <div
              className="card-premium max-w-3xl mx-auto p-10 text-center"
              data-testid="factuur-success"
            >
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h2 className="font-chivo font-black text-3xl text-brand-900 mt-4">
                {t("factuur.success_title")}
              </h2>
              <p className="text-ink-500 mt-3 max-w-xl mx-auto">
                {t("factuur.success_intro")}
              </p>
              <div className="mt-6 inline-flex flex-col items-center bg-brand-50 border border-brand-100 rounded-xl px-6 py-4">
                <span className="text-[11px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-500">
                  {t("factuur.success_number")}
                </span>
                <span
                  className="font-mono text-2xl text-brand-900 font-bold mt-1"
                  data-testid="factuur-success-number"
                >
                  {success.invoice_number}
                </span>
              </div>
              <p className="text-sm text-ink-400 mt-6" data-testid="factuur-email-status">
                {success.email_copy_status === "sent"
                  ? t("factuur.success_email_sent")
                  : t("factuur.success_email_skipped")}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Button
                  onClick={resetForm}
                  className="btn-primary"
                  data-testid="factuur-new-invoice"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("factuur.new_invoice")}
                </Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="grid lg:grid-cols-3 gap-8 items-start"
              data-testid="factuur-form"
            >
              <div className="lg:col-span-2 space-y-6">
                {/* Document settings */}
                <section className="card-premium p-6">
                  <h3 className="eyebrow text-brand-500">{t("factuur.lang_section")}</h3>
                  <div className="mt-3 grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="label-saas">{t("factuur.lang_section")}</Label>
                      <div className="mt-2 inline-flex bg-slate-100 rounded-xl p-1">
                        <button
                          type="button"
                          onClick={() => onChangeDocLang("nl")}
                          className={`px-4 py-1.5 text-sm font-chivo font-bold rounded-lg transition-colors ${
                            docLang === "nl" ? "bg-white text-brand-900 shadow-sm" : "text-ink-500"
                          }`}
                          data-testid="factuur-lang-nl"
                        >
                          {t("factuur.lang_nl")}
                        </button>
                        <button
                          type="button"
                          onClick={() => onChangeDocLang("en")}
                          className={`px-4 py-1.5 text-sm font-chivo font-bold rounded-lg transition-colors ${
                            docLang === "en" ? "bg-white text-brand-900 shadow-sm" : "text-ink-500"
                          }`}
                          data-testid="factuur-lang-en"
                        >
                          {t("factuur.lang_en")}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-saas">{t("factuur.currency_label")}</Label>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        className="select-saas mt-2"
                        data-testid="factuur-currency"
                      >
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="label-saas">{t("factuur.invoice_date")}</Label>
                      <Input
                        type="date"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                        className="input-saas mt-2"
                        data-testid="factuur-date"
                      />
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <Label className="label-saas">{t("factuur.logo_section")}</Label>
                    <p className="hint-saas mt-1">{t("factuur.logo_help")}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={onLogoChange}
                        className="hidden"
                        data-testid="factuur-logo-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        data-testid="factuur-logo-upload-btn"
                      >
                        {logoUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {t("factuur.logo_upload")}
                      </Button>
                      {logoId && (
                        <>
                          <span className="text-sm text-ink-500" data-testid="factuur-logo-filename">{logoFilename}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-red-600 rounded-xl"
                            onClick={clearLogo}
                            data-testid="factuur-logo-clear-btn"
                          >
                            <X className="w-4 h-4 mr-1" /> {t("factuur.logo_clear")}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </section>

                {/* Sender + Receiver */}
                <PartyCard
                  title={t("factuur.sender")}
                  testid="factuur-sender"
                  data={sender}
                  onChange={setParty("sender")}
                  onPatch={(patch) => setSender((s) => ({ ...s, ...patch }))}
                  emailRequired
                />
                <PartyCard
                  title={t("factuur.receiver")}
                  testid="factuur-receiver"
                  data={receiver}
                  onChange={setParty("receiver")}
                  onPatch={(patch) => setReceiver((s) => ({ ...s, ...patch }))}
                />

                {/* Shipment */}
                <section className="card-premium p-6" data-testid="factuur-shipment">
                  <h3 className="font-chivo font-black text-lg text-brand-900">{t("factuur.shipment")}</h3>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <Field label={`${t("factuur.field_destination")} *`}>
                      <Input
                        required
                        value={shipment.destination}
                        onChange={setShip("destination")}
                        className="input-saas"
                        data-testid="factuur-shipment-destination"
                      />
                    </Field>
                    <Field label={t("factuur.field_w_number")}>
                      <Input value={shipment.w_number} onChange={setShip("w_number")} className="input-saas" data-testid="factuur-shipment-wnumber" />
                    </Field>
                    <Field label={t("factuur.field_boxes")}>
                      <Input type="number" min="0" value={shipment.boxes} onChange={setShip("boxes")} className="input-saas" data-testid="factuur-shipment-boxes" />
                    </Field>
                    <Field label={t("factuur.field_description")} className="sm:col-span-2">
                      <Textarea rows={2} value={shipment.description} onChange={setShip("description")} className="textarea-saas" data-testid="factuur-shipment-description" />
                    </Field>
                    <Field label={t("factuur.field_remarks")} className="sm:col-span-2">
                      <Textarea rows={2} value={shipment.remarks} onChange={setShip("remarks")} className="textarea-saas" data-testid="factuur-shipment-remarks" />
                    </Field>
                  </div>
                </section>

                {/* Line items */}
                <section className="card-premium p-6" data-testid="factuur-lines">
                  <div className="flex items-center justify-between">
                    <h3 className="font-chivo font-black text-lg text-brand-900">{t("factuur.lines_title")}</h3>
                    <Button type="button" onClick={addLine} variant="outline" className="rounded-xl" data-testid="factuur-add-line">
                      <Plus className="w-4 h-4 mr-1" /> {t("factuur.add_line")}
                    </Button>
                  </div>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-[11px] uppercase tracking-wider font-chivo font-bold text-ink-400">
                        <tr>
                          <th className="text-left pb-2 pr-2 w-[44%]">{t("factuur.col_desc")}</th>
                          <th className="text-right pb-2 pr-2 w-[14%]">{t("factuur.col_qty")}</th>
                          <th className="text-right pb-2 pr-2 w-[18%]">{t("factuur.col_unit")}</th>
                          <th className="text-right pb-2 pr-2 w-[18%]">{t("factuur.col_total")}</th>
                          <th className="pb-2 w-[6%]" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {lines.map((l, idx) => {
                          const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                          const descLen = (l.description || "").length;
                          const isLong = descLen > 100 && descLen <= 300;
                          const isExtreme = descLen > 300;
                          return (
                            <tr key={idx} data-testid={`factuur-line-${idx}`}>
                              <td className="py-2 pr-2">
                                <Input
                                  value={l.description}
                                  onChange={(e) => setLine(idx, "description", e.target.value)}
                                  className="input-saas"
                                  data-testid={`factuur-line-desc-${idx}`}
                                />
                                {descLen > 0 && (
                                  <div
                                    className={`mt-1 text-[11px] ${
                                      isExtreme
                                        ? "text-amber-600"
                                        : isLong
                                          ? "text-slate-500"
                                          : "text-slate-400"
                                    }`}
                                    data-testid={`factuur-line-desc-hint-${idx}`}
                                  >
                                    {descLen} tekens
                                    {isLong && " · Wordt over meerdere regels geplaatst op de PDF."}
                                    {isExtreme && " · Erg lang — wordt als aparte toelichting onderaan de factuur getoond."}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 pr-2 text-right">
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={l.quantity}
                                  onChange={(e) => setLine(idx, "quantity", e.target.value)}
                                  className="input-saas text-right"
                                  data-testid={`factuur-line-qty-${idx}`}
                                />
                              </td>
                              <td className="py-2 pr-2 text-right">
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={l.unit_price}
                                  onChange={(e) => setLine(idx, "unit_price", e.target.value)}
                                  className="input-saas text-right"
                                  data-testid={`factuur-line-unit-${idx}`}
                                />
                              </td>
                              <td className="py-2 pr-2 text-right font-mono text-brand-900" data-testid={`factuur-line-total-${idx}`}>
                                {fmtMoney(lineTotal, currency)}
                              </td>
                              <td className="py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500"
                                  disabled={lines.length === 1}
                                  onClick={() => removeLine(idx)}
                                  aria-label={t("factuur.remove_line")}
                                  data-testid={`factuur-line-remove-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="label-saas mb-0">{t("factuur.vat_label")}</Label>
                      <Input
                        type="number" min="0" max="100" step="0.1"
                        value={vatPercent}
                        onChange={(e) => setVatPercent(e.target.value)}
                        className="input-saas w-24 text-right"
                        data-testid="factuur-vat-percent"
                      />
                    </div>
                  </div>

                  <div className="mt-4 ml-auto max-w-xs space-y-1.5">
                    <div className="flex justify-between text-sm text-ink-500">
                      <span>{t("factuur.subtotal")}</span>
                      <span className="font-mono text-brand-900" data-testid="factuur-subtotal">{fmtMoney(totals.subtotal, currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-ink-500">
                      <span>{t("factuur.vat_amount")} ({Number(vatPercent) || 0}%)</span>
                      <span className="font-mono text-brand-900" data-testid="factuur-vat-amount">{fmtMoney(totals.vatAmount, currency)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                      <span className="text-brand-900">{t("factuur.total")}</span>
                      <span className="font-mono text-brand-900" data-testid="factuur-total">{fmtMoney(totals.total, currency)}</span>
                    </div>
                  </div>
                </section>

                {/* Declarations */}
                <section className="card-premium p-6" data-testid="factuur-declarations">
                  <h3 className="font-chivo font-black text-lg text-brand-900">{t("factuur.declarations_title")}</h3>
                  <div className="mt-4 space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={decls.value_declaration}
                        onCheckedChange={(v) => setDecls((d) => ({ ...d, value_declaration: !!v }))}
                        data-testid="factuur-decl-value"
                      />
                      <span className="text-sm text-ink-700 leading-relaxed">{t("factuur.decl_value")}</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={decls.truthful}
                        onCheckedChange={(v) => setDecls((d) => ({ ...d, truthful: !!v }))}
                        data-testid="factuur-decl-truthful"
                      />
                      <span className="text-sm text-ink-700 leading-relaxed">{t("factuur.decl_truthful")}</span>
                    </label>
                  </div>
                </section>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-xl text-sm" data-testid="factuur-error">
                    {error}
                  </div>
                )}
              </div>

              {/* Sticky submit panel */}
              <aside className="lg:col-span-1 lg:sticky lg:top-24">
                <div className="card-dark p-6 text-white space-y-4">
                  <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase font-chivo font-bold text-brand-200">
                      {t("factuur.total")}
                    </div>
                    <div className="font-mono text-3xl font-bold mt-1" data-testid="factuur-aside-total">
                      {fmtMoney(totals.total, currency)}
                    </div>
                    <p className="text-xs text-white/60 mt-2">{t("factuur.hint_pdf_download")}</p>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary-lg w-full"
                    data-testid="factuur-submit"
                  >
                    {submitting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("factuur.generating")}</>
                      : <><FileDown className="w-4 h-4 mr-2" /> {t("factuur.submit")}</>}
                  </Button>
                  {submitting && (
                    <p
                      className="text-xs text-brand-100/80 mt-2 leading-snug"
                      data-testid="factuur-submit-hint"
                    >
                      {t("factuur.hint_generating")}
                      {pdf.isSlow && (
                        <>
                          {" "}
                          <span data-testid="factuur-submit-slow-hint">{pdf.slowHint}</span>
                        </>
                      )}
                    </p>
                  )}
                </div>
              </aside>
            </form>
          )}
        </div>
        <ToolPageCtaBand cta={cms?.cta_band || FALLBACK_CMS.cta_band} testid="factuur-cta-band" />
      </main>
    </>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-saas">{label}</span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

function PartyCard({ title, testid, data, onChange, onPatch, emailRequired = false }) {
  const { t } = useI18n();
  return (
    <section className="card-premium p-6" data-testid={testid}>
      <h3 className="font-chivo font-black text-lg text-brand-900">{title}</h3>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label={`${t("factuur.field_name")} *`}>
          <Input required value={data.name} onChange={onChange("name")} className="input-saas" data-testid={`${testid}-name`} />
        </Field>
        <Field label={t("factuur.field_company")}>
          <Input value={data.company} onChange={onChange("company")} className="input-saas" data-testid={`${testid}-company`} />
        </Field>
      </div>

      {/* P1 — NAW-blok met PDOK-postcode/huisnummer lookup voor NL adressen.
          variant="public" routeert via /api/checkout/postcode-lookup (geen
          auth nodig) zodat de publieke factuurmaker werkt zonder admin-login.
          `street` wordt op submit gecomposeerd naar `address` voor de
          bestaande PDF-builder. */}
      <div className="mt-4">
        <NAWAddressBlock
          variant="public"
          value={{
            street: data.street || data.address || "",
            house_number: data.house_number || "",
            house_number_addition: data.house_number_addition || "",
            postal_code: data.postal_code || data.postcode || "",
            city: data.city || "",
            country: data.country || "",
          }}
          onChange={(patch) => {
            if (!onPatch) return;
            const next = {};
            if ("street" in patch) { next.street = patch.street; next.address = patch.street; }
            if ("house_number" in patch) next.house_number = patch.house_number;
            if ("house_number_addition" in patch) next.house_number_addition = patch.house_number_addition;
            if ("postal_code" in patch) { next.postal_code = patch.postal_code; next.postcode = patch.postal_code; }
            if ("city" in patch) next.city = patch.city;
            if ("country" in patch) next.country = patch.country;
            onPatch(next);
          }}
          testidPrefix={testid}
          title={`${t("factuur.field_address")} *`}
          hint="Vul postcode + huisnummer in voor automatisch ophalen van straat en plaats (NL)."
        />
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label={`${t("factuur.field_email")}${emailRequired ? " *" : ""}`}>
          <Input
            type="email"
            required={emailRequired}
            value={data.email}
            onChange={onChange("email")}
            className="input-saas"
            data-testid={`${testid}-email`}
          />
        </Field>
        <Field label={t("factuur.field_phone")}>
          <Input value={data.phone} onChange={onChange("phone")} className="input-saas" data-testid={`${testid}-phone`} />
        </Field>
      </div>
    </section>
  );
}
